/**
 * Cifrado opcional de los respaldos (design.md, 10.9; Req. 6.18 a 6.23).
 *
 * Aprobado por Janeth: desactivado al inicio. Con WebCrypto, sin librerías:
 * - Clave: PBKDF2-SHA-256 con sal aleatoria de 16 bytes y 600,000 iteraciones
 *   (la recomendación de OWASP para PBKDF2-SHA-256). Las iteraciones viajan
 *   en el archivo para poder subirlas más adelante.
 * - Cifrado: AES-GCM de 256 bits con IV aleatorio de 12 bytes. GCM detecta
 *   cualquier alteración del archivo, incluida la de su cabecera, que va como
 *   dato autenticado.
 * - Verificador: un valor derivado de la contraseña con su propia sal. Sirve
 *   para detectar un error de escritura antes de cifrar. La contraseña NUNCA
 *   se guarda.
 *
 * Si se olvida la contraseña no hay forma de abrir los respaldos cifrados con
 * ella: ni la app ni nadie. Por eso la pantalla lo advierte y pide confirmarlo.
 *
 * Código puro: no toca IndexedDB.
 */

import { ErrorRespaldo } from '@/domain/errors';
import { ahoraIso } from '@/domain/dates';
import {
  FORMATO_CIFRADO,
  IDENTIFICADOR_APP,
  MENSAJE_VERSION_NUEVA,
  nombreArchivoRespaldo,
  verificarRespaldoGenerado,
  type ArchivoRespaldo,
} from './backup-format';
import type { Bytes, CifradoRespaldo } from './types';

export const ITERACIONES_PBKDF2 = 600_000;
/** Tope al leer un archivo: un valor absurdo dejaría el teléfono trabado. */
const ITERACIONES_MAXIMAS = 10_000_000;
const BYTES_SAL = 16;
const BYTES_IV = 12;
export const LARGO_MINIMO_CONTRASENA = 8;

export const ALGORITMO = 'AES-GCM-256';
export const DERIVACION = 'PBKDF2-SHA-256';

export interface EnvolturaCifrada {
  app: string;
  formatVersion: typeof FORMATO_CIFRADO;
  exportedAt: string;
  cifrado: {
    alg: typeof ALGORITMO;
    kdf: typeof DERIVACION;
    iteraciones: number;
    /** base64 */
    sal: string;
    /** base64 */
    iv: string;
  };
  /** base64 del ArchivoRespaldo (formato 1) cifrado. */
  contenido: string;
}

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

const sutil = () => globalThis.crypto.subtle;

function bytesAleatorios(cantidad: number): Uint8Array<ArrayBuffer> {
  return globalThis.crypto.getRandomValues(new Uint8Array(cantidad));
}

/** En trozos: `String.fromCharCode(...bytes)` desborda la pila con archivos grandes. */
export function aBase64(bytes: Uint8Array): string {
  let binario = '';
  const trozo = 0x8000;
  for (let i = 0; i < bytes.length; i += trozo) {
    binario += String.fromCharCode(...bytes.subarray(i, i + trozo));
  }
  return btoa(binario);
}

export function deBase64(texto: string): Uint8Array<ArrayBuffer> {
  let binario: string;
  try {
    binario = atob(texto);
  } catch {
    throw new ErrorRespaldo('El respaldo cifrado está dañado. No se modificó ningún dato.');
  }
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes;
}

function iguales(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diferencia = 0;
  for (let i = 0; i < a.length; i++) diferencia |= (a[i] ?? 0) ^ (b[i] ?? 0);
  return diferencia === 0;
}

/**
 * La contraseña se normaliza a NFC: una "ñ" o una tilde pueden llegar como un
 * carácter o como dos (letra + acento) según de dónde se pegue, y sin esto
 * darían claves distintas. Va desde el primer archivo cifrado; cambiarlo
 * después dejaría sin abrir los archivos anteriores.
 */
async function materialDe(contrasena: string): Promise<CryptoKey> {
  return sutil().importKey('raw', new TextEncoder().encode(contrasena.normalize('NFC')), 'PBKDF2', false, [
    'deriveBits',
    'deriveKey',
  ]);
}

async function derivarClave(
  contrasena: string,
  sal: Uint8Array<ArrayBuffer>,
  iteraciones: number,
): Promise<CryptoKey> {
  return sutil().deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: sal, iterations: iteraciones },
    await materialDe(contrasena),
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function derivarVerificador(
  contrasena: string,
  sal: Uint8Array<ArrayBuffer>,
  iteraciones: number,
): Promise<Uint8Array> {
  const bits = await sutil().deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: sal, iterations: iteraciones },
    await materialDe(contrasena),
    256,
  );
  return new Uint8Array(bits);
}

/** La cabecera va autenticada: cambiar la fecha o la app también se detecta. */
function datosAutenticados(e: Pick<EnvolturaCifrada, 'app' | 'formatVersion' | 'exportedAt'>) {
  return new TextEncoder().encode(`${e.app}|${e.formatVersion}|${e.exportedAt}`);
}

// ---------------------------------------------------------------------------
// Contraseña y verificador (Req. 6.19)
// ---------------------------------------------------------------------------

/** Mensaje de error de una contraseña nueva escrita dos veces, o null si sirve. */
export function validarContrasenaNueva(contrasena: string, repetida: string): string | null {
  if (contrasena.trim().length < LARGO_MINIMO_CONTRASENA) {
    return `La contraseña debe tener al menos ${LARGO_MINIMO_CONTRASENA} caracteres.`;
  }
  if (contrasena !== repetida) return 'Las dos contraseñas no son iguales.';
  return null;
}

export async function crearVerificador(
  contrasena: string,
  iteraciones = ITERACIONES_PBKDF2,
  activadoEn = ahoraIso(),
): Promise<CifradoRespaldo> {
  const sal = bytesAleatorios(BYTES_SAL);
  const verificador = await derivarVerificador(contrasena, sal, iteraciones);
  return { sal: aBase64(sal), verificador: aBase64(verificador), iteraciones, activadoEn };
}

/** Compara con el verificador guardado, sin conocer la contraseña configurada. */
export async function contrasenaCorrecta(
  contrasena: string,
  cifrado: CifradoRespaldo,
): Promise<boolean> {
  const calculado = await derivarVerificador(contrasena, deBase64(cifrado.sal), cifrado.iteraciones);
  return iguales(calculado, deBase64(cifrado.verificador));
}

// ---------------------------------------------------------------------------
// Cifrar y descifrar
// ---------------------------------------------------------------------------

/**
 * Cifra unos bytes cualesquiera. Es la base de todo: el respaldo en JSON
 * entra como texto UTF-8 y el respaldo con fotos entra como el ZIP completo
 * (design.md 10.7).
 *
 * La envoltura NO dice qué va dentro, a propósito: la cabecera va autenticada
 * y agregarle un campo cambiaría los datos autenticados, así que los archivos
 * cifrados que ya existen dejarían de abrirse. Al descifrar se reconoce por
 * los primeros bytes: `{` es el JSON y `PK` es el ZIP.
 */
export async function cifrarBytes(
  bytes: Bytes,
  contrasena: string,
  opciones: { iteraciones?: number; exportedAt?: string } = {},
): Promise<EnvolturaCifrada> {
  const iteraciones = opciones.iteraciones ?? ITERACIONES_PBKDF2;
  const sal = bytesAleatorios(BYTES_SAL);
  const iv = bytesAleatorios(BYTES_IV);
  const cabecera = {
    app: IDENTIFICADOR_APP,
    formatVersion: FORMATO_CIFRADO,
    exportedAt: opciones.exportedAt ?? ahoraIso(),
  } as const;

  const clave = await derivarClave(contrasena, sal, iteraciones);
  const cifrado = await sutil().encrypt(
    { name: 'AES-GCM', iv, additionalData: datosAutenticados(cabecera) },
    clave,
    bytes,
  );

  return {
    ...cabecera,
    cifrado: { alg: ALGORITMO, kdf: DERIVACION, iteraciones, sal: aBase64(sal), iv: aBase64(iv) },
    contenido: aBase64(new Uint8Array(cifrado)),
  };
}

export async function cifrarTexto(
  texto: string,
  contrasena: string,
  opciones: { iteraciones?: number; exportedAt?: string } = {},
): Promise<EnvolturaCifrada> {
  return cifrarBytes(new TextEncoder().encode(texto), contrasena, opciones);
}

export function serializarEnvoltura(envoltura: EnvolturaCifrada): string {
  return JSON.stringify(envoltura, null, 2);
}

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

/**
 * Si el texto es un respaldo cifrado de DuoFit, devuelve su envoltura; si es
 * cualquier otra cosa devuelve null y el análisis normal decide. Lanza si dice
 * ser un respaldo cifrado pero está incompleto.
 */
export function leerEnvolturaCifrada(texto: string): EnvolturaCifrada | null {
  let crudo: unknown;
  try {
    crudo = JSON.parse(texto);
  } catch {
    return null;
  }
  if (!esObjeto(crudo) || crudo['app'] !== IDENTIFICADOR_APP) return null;
  if (crudo['formatVersion'] !== FORMATO_CIFRADO) return null;

  const danado = new ErrorRespaldo(
    'El respaldo cifrado está incompleto o dañado. No se modificó ningún dato.',
  );
  const cifrado = crudo['cifrado'];
  if (!esObjeto(cifrado) || typeof crudo['contenido'] !== 'string') throw danado;
  if (cifrado['alg'] !== ALGORITMO || cifrado['kdf'] !== DERIVACION) {
    throw new ErrorRespaldo(MENSAJE_VERSION_NUEVA);
  }
  const iteraciones = cifrado['iteraciones'];
  if (
    typeof iteraciones !== 'number' ||
    !Number.isInteger(iteraciones) ||
    iteraciones < 1 ||
    iteraciones > ITERACIONES_MAXIMAS
  ) {
    throw danado;
  }
  if (typeof cifrado['sal'] !== 'string' || typeof cifrado['iv'] !== 'string') throw danado;
  if (deBase64(cifrado['sal']).length !== BYTES_SAL || deBase64(cifrado['iv']).length !== BYTES_IV) {
    throw danado;
  }

  return {
    app: IDENTIFICADOR_APP,
    formatVersion: FORMATO_CIFRADO,
    exportedAt: typeof crudo['exportedAt'] === 'string' ? crudo['exportedAt'] : '',
    cifrado: {
      alg: ALGORITMO,
      kdf: DERIVACION,
      iteraciones,
      sal: cifrado['sal'],
      iv: cifrado['iv'],
    },
    contenido: crudo['contenido'],
  };
}

/**
 * Descifra con la contraseña escrita. Con contraseña incorrecta o archivo
 * alterado, GCM no autentica y se lanza un mensaje claro (Req. 6.22); los
 * dos casos no se pueden distinguir, y el mensaje lo dice.
 */
export async function descifrarBytes(
  envoltura: EnvolturaCifrada,
  contrasena: string,
): Promise<Bytes> {
  const { iteraciones, sal, iv } = envoltura.cifrado;
  try {
    const clave = await derivarClave(contrasena, deBase64(sal), iteraciones);
    const plano = await sutil().decrypt(
      { name: 'AES-GCM', iv: deBase64(iv), additionalData: datosAutenticados(envoltura) },
      clave,
      deBase64(envoltura.contenido),
    );
    return new Uint8Array(plano);
  } catch {
    throw new ErrorRespaldo(
      'No se pudo abrir el respaldo: la contraseña no es la correcta o el archivo se ' +
        'modificó. No se modificó ningún dato.',
    );
  }
}

/**
 * Lo mismo, cuando dentro va el respaldo en JSON. Si el archivo cifrado
 * resulta traer un ZIP, lo dice en lugar de devolver caracteres ilegibles.
 */
export async function descifrarTexto(
  envoltura: EnvolturaCifrada,
  contrasena: string,
): Promise<string> {
  const plano = await descifrarBytes(envoltura, contrasena);
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(plano);
  } catch {
    throw new ErrorRespaldo(
      'Ese respaldo cifrado trae un archivo con fotos, no el respaldo en JSON. ' +
        'No se modificó ningún dato.',
    );
  }
}

// ---------------------------------------------------------------------------
// Exportación cifrada (Req. 6.21)
// ---------------------------------------------------------------------------

export interface RespaldoCifradoPreparado {
  texto: string;
  nombre: string;
}

/**
 * Cifra un respaldo ya generado y verificado:
 * 1. comprueba la contraseña escrita contra el verificador;
 * 2. cifra;
 * 3. descifra el archivo resultante con la misma contraseña y lo vuelve a
 *    verificar (suma y conteos) antes de entregarlo.
 */
export async function cifrarRespaldo(
  preparado: { texto: string; archivo: ArchivoRespaldo },
  contrasena: string,
  configurado: CifradoRespaldo,
  opciones: { fecha?: Date; iteraciones?: number } = {},
): Promise<RespaldoCifradoPreparado> {
  if (!(await contrasenaCorrecta(contrasena, configurado))) {
    throw new ErrorRespaldo(
      'La contraseña no es la que configuraste para los respaldos. Revisa cómo la escribiste.',
    );
  }

  const envoltura = await cifrarTexto(preparado.texto, contrasena, {
    exportedAt: preparado.archivo.exportedAt,
    ...(opciones.iteraciones !== undefined ? { iteraciones: opciones.iteraciones } : {}),
  });
  const texto = serializarEnvoltura(envoltura);

  // Prueba de descifrado: lo que se entrega se puede abrir y está completo.
  const leida = leerEnvolturaCifrada(texto);
  if (leida === null) throw new ErrorRespaldo('No se pudo verificar el respaldo cifrado.');
  const descifrado = await descifrarTexto(leida, contrasena);
  if (descifrado !== preparado.texto) {
    throw new ErrorRespaldo('El respaldo cifrado no coincide con el original. No se entregó.');
  }
  await verificarRespaldoGenerado(descifrado, preparado.archivo.counts);

  return { texto, nombre: nombreArchivoRespaldo(opciones.fecha ?? new Date(), true) };
}
